import { AdminGameFilterInfo, GameFilterAvailability } from "../../models/gameFilters";

export function scalarToString(value: unknown, fallback: string = ""): string {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") return String(value);
    const maybeToString = (value as any)?.toString;
    if (typeof maybeToString === "function") {
        const res = maybeToString.call(value);
        return typeof res === "string" ? res : String(res);
    }
    return String(value);
}

export function toNumber(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export function parseNullableNumber(input: string): number | null {
    const s = (input ?? "").trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

export function normalizeColor(value: unknown): string {
    const raw = scalarToString(value).trim();
    if (!raw) return "";
    return raw.startsWith("#") ? raw : `#${raw}`;
}

export function stripColorHash(value: unknown): string {
    const raw = scalarToString(value).trim();
    if (!raw) return "";
    return raw.startsWith("#") ? raw.slice(1) : raw;
}

export function parseTwoColors(value: unknown): { first?: string; second?: string } {
    const raw = scalarToString(value).trim();
    if (!raw) return {};
    const parts = raw.split(",").map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) return { first: normalizeColor(parts[0]) };
    return {
        first: normalizeColor(parts[0]),
        second: normalizeColor(parts[1]),
    };
}

export function renderFilterRegion(region: unknown): string {
    const raw = scalarToString(region);
    const num = Number(raw);
    if (Number.isFinite(num)) {
        switch (num) {
            case 0:
                return "Верхнее меню";
            case 1:
                return "Боковое";
            default:
                return `Регион (${num})`;
        }
    }
    return raw || "-";
}

export function renderAvailability(avail: unknown): string {
    const raw = scalarToString(avail);
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw || "-";

    const parts: string[] = [];
    if (n & GameFilterAvailability.BuyPage) parts.push("Страница купить");
    if (n & GameFilterAvailability.PendingPage) parts.push("Страница ожидания");
    if (n & GameFilterAvailability.InventoryPage) parts.push("Страница инвентаря");
    if (n & GameFilterAvailability.SalePage) parts.push("Страница продажи");
    if (n & GameFilterAvailability.PlaceBidPage) parts.push("Разместить заявку");
    if (n & GameFilterAvailability.MyOrdersPage) parts.push("Страница мои заявки");
    if (n & GameFilterAvailability.CreateOrder) parts.push("Модалка создания заявки на покупку");
    if (n & GameFilterAvailability.Profile) parts.push("Публичный профиль");
    if (n & GameFilterAvailability.AllBidsPage) parts.push("Все заявки");
    if (n & GameFilterAvailability.SkinPage) parts.push("Страница товара");
    return parts.length ? parts.join(", ") : "Не используется";
}

export function renderColorShape(color: unknown, colorType: unknown, circular = false) {
    const { first, second } = parseTwoColors(color);
    if (!first) return null;
    const typeNum = Number(scalarToString(colorType || 0));
    const fill =
        second
            ? `linear-gradient(to right, ${first} 0 50%, ${second} 50% 100%)`
            : first;

    if (typeNum === 2) {
        return {
            variant: "star" as const,
            fill,
            first,
            second,
        };
    }

    const borderRadius = circular || typeNum === 1 ? "50%" : "3px";

    return {
        variant: "shape" as const,
        fill,
        borderRadius,
        first,
        second,
    };
}

export function withParens(primary?: string | null, secondary?: string | null, emptyFallback: string = "(без названия)"): string {
    const p = (primary ?? "").trim();
    const s = (secondary ?? "").trim();
    if (p && s) return `${p} (${s})`;
    if (p) return p;
    if (s) return s;
    return emptyFallback;
}

export function computeAvailabilityFlags(
    buy: boolean,
    pending: boolean,
    inventory: boolean,
    sale: boolean,
    placeBid: boolean,
    myOrders: boolean,
    createOrder: boolean,
    profile: boolean,
    allBids: boolean,
    skin: boolean,
): number {
    return (
        (buy ? GameFilterAvailability.BuyPage : 0) |
        (pending ? GameFilterAvailability.PendingPage : 0) |
        (inventory ? GameFilterAvailability.InventoryPage : 0) |
        (sale ? GameFilterAvailability.SalePage : 0) |
        (placeBid ? GameFilterAvailability.PlaceBidPage : 0) |
        (myOrders ? GameFilterAvailability.MyOrdersPage : 0) |
        (createOrder ? GameFilterAvailability.CreateOrder : 0) |
        (profile ? GameFilterAvailability.Profile : 0) |
        (allBids ? GameFilterAvailability.AllBidsPage : 0) |
        (skin ? GameFilterAvailability.SkinPage : 0)
    );
}

export function splitAvailabilityFlags(avail: unknown): {
    buy: boolean;
    pending: boolean;
    inventory: boolean;
    sale: boolean;
    placeBid: boolean;
    myOrders: boolean;
    createOrder: boolean;
    profile: boolean;
    allBids: boolean;
    skin: boolean;
} {
    const value = toNumber(avail, 0);
    return {
        buy: (value & GameFilterAvailability.BuyPage) === GameFilterAvailability.BuyPage,
        pending: (value & GameFilterAvailability.PendingPage) === GameFilterAvailability.PendingPage,
        inventory: (value & GameFilterAvailability.InventoryPage) === GameFilterAvailability.InventoryPage,
        sale: (value & GameFilterAvailability.SalePage) === GameFilterAvailability.SalePage,
        placeBid: (value & GameFilterAvailability.PlaceBidPage) === GameFilterAvailability.PlaceBidPage,
        myOrders: (value & GameFilterAvailability.MyOrdersPage) === GameFilterAvailability.MyOrdersPage,
        createOrder: (value & GameFilterAvailability.CreateOrder) === GameFilterAvailability.CreateOrder,
        profile: (value & GameFilterAvailability.Profile) === GameFilterAvailability.Profile,
        allBids: (value & GameFilterAvailability.AllBidsPage) === GameFilterAvailability.AllBidsPage,
        skin: (value & GameFilterAvailability.SkinPage) === GameFilterAvailability.SkinPage,
    };
}

export type FiltersGroup = {
    key: string;
    groupNameRu: string;
    groupNameEn: string;
    groupType: string;
    filterRegion: AdminGameFilterInfo["filterRegion"];
    groupOrder: number;
    items: AdminGameFilterInfo[];
};

export function groupFilters(filters: AdminGameFilterInfo[]): FiltersGroup[] {
    const map = new Map<string, FiltersGroup>();

    for (const f of filters) {
        const key = `${scalarToString(f.filterRegion)}::${f.groupOrder}::${f.groupType}::${f.groupNameRu}`;
        const existing = map.get(key);
        if (existing) {
            existing.items.push(f);
        } else {
            map.set(key, {
                key,
                groupNameRu: f.groupNameRu,
                groupNameEn: f.groupNameEn,
                groupType: f.groupType,
                filterRegion: f.filterRegion,
                groupOrder: f.groupOrder,
                items: [f],
            });
        }
    }

    return Array.from(map.values())
        .sort((a, b) => {
            const ar = Number(scalarToString(a.filterRegion || 0));
            const br = Number(scalarToString(b.filterRegion || 0));
            if (Number.isFinite(ar) && Number.isFinite(br) && ar !== br) {
                return ar - br;
            }
            if (Number.isFinite(ar) !== Number.isFinite(br)) {
                return Number.isFinite(ar) ? -1 : 1;
            }
            return a.groupOrder - b.groupOrder;
        })
        .map(g => ({
            ...g,
            items: [...g.items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        }));
}

